import { QuoteStatus } from '@/types';
import type { Quote } from '@/types';
import { hubspotRecordUrl } from '@/lib/hubspotContext';

/**
 * Alles wat zowel de lijst- als de kaartweergave over een offerte moet weten.
 *
 * Beide weergaven tonen dezelfde offerte; als de statuslabels of de
 * eigenaarsnaam per weergave zouden verschillen, gaat de gebruiker twijfelen of
 * hij naar hetzelfde record kijkt. Daarom staan ze hier één keer.
 */

export const STATUS_LABELS: Record<string, string> = {
  DRAFT: 'Concept',
  SENT: 'Gepubliceerd',
  VIEWED: 'Bekeken',
  ACCEPTED: 'Geaccepteerd',
  DECLINED: 'Afgewezen',
  EXPIRED: 'Verlopen',
  WITHDRAWN: 'Ingetrokken',
};

export const STATUS_VARIANT: Record<
  string,
  'default' | 'secondary' | 'destructive' | 'outline' | 'success' | 'warning' | 'info'
> = {
  DRAFT: 'secondary',
  SENT: 'info',
  VIEWED: 'default',
  ACCEPTED: 'success',
  DECLINED: 'destructive',
  EXPIRED: 'warning',
  WITHDRAWN: 'secondary',
};

export const OPEN_QUOTE_STATUSES = [QuoteStatus.SENT, QuoteStatus.VIEWED];

export function isOpenQuoteStatus(status: QuoteStatus | string) {
  return OPEN_QUOTE_STATUSES.includes(status as QuoteStatus);
}

export function getQuoteDealLabel(quote: Quote) {
  if (quote.dealName) return quote.dealName;
  if (quote.dealId) return `Deal ${quote.dealId}`;
  return null;
}

export function getQuoteOwnerLabel(quote: Quote) {
  const ownerName = quote.dealOwnerName?.trim();
  const ownerId = quote.dealOwnerId?.trim();
  if (ownerName && ownerName !== ownerId) return ownerName;
  if (ownerId) return `Onbekende eigenaar (${ownerId})`;
  return null;
}

export function getQuoteOwnerSearchText(quote: Quote) {
  return [getQuoteOwnerLabel(quote), quote.dealOwnerName, quote.dealOwnerId]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
}

export function getUboSignerLabel(quote: Quote) {
  const summary = quote.signatureSummary;
  if (!summary) return null;
  if (summary.senderSignerName) return summary.senderSignerName;
  if (summary.senderSignerId) return `UBO-tekenaar ${summary.senderSignerId}`;
  return null;
}

export function getCountersignActionLabel(quote: Quote) {
  const signerLabel = getUboSignerLabel(quote);
  return signerLabel ? `Link voor ${signerLabel} openen` : 'Medeonderteken-link openen';
}

export function getQuoteDealUrl(quote: Quote, portalIdFallback?: string) {
  if (!quote.dealId) return null;
  const portalId = quote.portalId || portalIdFallback;
  if (!portalId) return null;
  return hubspotRecordUrl(portalId, '0-3', quote.dealId);
}

/** Nul views is pas een signaal zodra de klant de offerte hoort te kunnen zien. */
export function getViewLabel(quote: Quote) {
  if ((quote.viewCount ?? 0) > 0) return `${quote.viewCount}× bekeken`;
  return quote.status === QuoteStatus.SENT ? 'nog niet geopend' : null;
}

/** Een gearchiveerde offerte heeft geen werkende klantlink, dus valt er niets te bekijken. */
export function canViewQuote(quote: Quote) {
  return Boolean(quote.slug) && !quote.archivedAt;
}

export function getViewButtonTitle(quote: Quote) {
  if (quote.archivedAt) return 'Gearchiveerd: de klantlink staat uit';
  return quote.slug ? 'Bekijken: opent de klantweergave' : 'Nog geen publieke link';
}

export const dutchDate = (value: string | Date) => new Date(value).toLocaleDateString('nl-NL');
